import React from 'react';
import PropTypes from 'prop-types';
import {
  Typography, Link, CardMedia, Alert, IconButton
} from '@one-thd/sui-atomic-components';
import { ArrowForward } from '@one-thd/sui-icons';
import classNames from 'classnames';
import { AddMaterialsToYourList } from './AddMaterialsToYourList';

const ThumbnailsView = ({
  images, numberOfThumbnails, url, handleEmptyListNavigation, onImageClick
}) => {
  const isEmptyList = images?.length === 0 || images === null;

  const thumbnailsWrapperClass = classNames(
    'sui-grid sui-gap-2 sui-h-full',
    {
      'sui-grid-cols-1': isEmptyList || numberOfThumbnails === 1,
      'sui-grid-cols-2': !isEmptyList && numberOfThumbnails === 2,
      'sui-grid-cols-3': !isEmptyList && numberOfThumbnails === 3,
      'sui-grid-cols-4': !isEmptyList && numberOfThumbnails === 4,
      'sui-grid-cols-5': !isEmptyList && numberOfThumbnails >= 5
    }
  );

  const ThumbnailsElement = () => (
    <div className={thumbnailsWrapperClass}>
      {!isEmptyList ? (
        Array.from({ length: numberOfThumbnails }, (__, thumbnailIndex) => {
          const thumbnailUrl = images?.length > thumbnailIndex && images?.[thumbnailIndex]?.url;
          return (
            <div key={`list-tile-thumbnail-${thumbnailIndex}`}>
              {thumbnailUrl ? (
                <CardMedia className="sui-w-full sui-aspect-square" src={thumbnailUrl} alt="Product Image" />
              ) : (
                <div className="sui-w-full sui-bg-strong sui-aspect-square" />
              )}
            </div>
          );
        })
      ) : (
        <div
          className="sui-col-span-full sui-w-full sui-h-full sui-flex sui-items-stretch"
          data-testid="empty-list-alert-container"
        >
          <Alert
            status="info"
            size="large"
            headline="Complete your list"
            action={(
              <IconButton
                icon={ArrowForward}
                iconSize="small"
                aria-label="View list details"
                title="View list details"
                onClick={handleEmptyListNavigation}
              />
            )}
          >
            You started building this list but haven&apos;t selected any products.
          </Alert>
        </div>
      )}
    </div>
  );

  return (
    <Link
      className="sui-w-full sui-h-full"
      href={url}
      // The empty state's alert sits inside this link, so only track real thumbnails.
      onClick={isEmptyList ? undefined : onImageClick}
      data-testid="alert-list-link"
    >
      <ThumbnailsElement />
    </Link>
  );
};

const ListImages = ({
  images,
  itemCount,
  numberOfThumbnails,
  url,
  disableThumbnailsLink = false,
  viewMode = 'grid',
  handleEmptyListNavigation,
  onImageClick
}) => {
  // Thumbnails grid view
  if (viewMode === 'thumbnails') {
    return (
      <ThumbnailsView
        images={images}
        numberOfThumbnails={numberOfThumbnails}
        url={url}
        handleEmptyListNavigation={handleEmptyListNavigation}
        onImageClick={onImageClick}
      />
    );
  }

  // Defensive check: show empty thumbnails if images are not available or itemCount is 0
  // Only applies to grid view
  if (!itemCount || !images || !Array.isArray(images) || images.length === 0) {
    return <AddMaterialsToYourList url={url} />;
  }

  // Default grid view (existing layout)
  const renderImage = (image, index) => {
    const imgElement = (
      <img
        key={`list-image-${index}`}
        src={image.url}
        alt={`List item ${index + 1}`}
        width="auto"
        height="auto"
        className="sui-w-14 @sm:sui-w-20 xl:@xl:sui-w-24 sui-aspect-square sui-object-cover"
        data-testid={`list-image-${index}`}
      />
    );

    return !disableThumbnailsLink && url ? (
      <Link key={`list-image-link-${index}`} href={url} onClick={onImageClick}>
        {imgElement}
      </Link>
    ) : (
      imgElement
    );
  };

  return (
    <div className="sui-flex sui-flex-nowrap sui-gap-2 sui-overflow-x-hidden">
      {images.slice(0, numberOfThumbnails).map((image, index) => renderImage(image, index))}
      {itemCount > numberOfThumbnails && (
        <div
          className="sui-w-14 @sm:sui-w-20 @xl:sui-w-24 sui-aspect-square sui-grid
          sui-place-items-center sui-rounded-md sui-border-1 sui-border-solid sui-border-primary"
          data-testid="list-image-more"
        >
          <Typography variant="body-base" color="subtle">+{itemCount - numberOfThumbnails}</Typography>
        </div>
      )}
    </div>
  );
};

ThumbnailsView.propTypes = {
  images: PropTypes.arrayOf(PropTypes.shape({
    url: PropTypes.string
  })),
  numberOfThumbnails: PropTypes.number.isRequired,
  url: PropTypes.string,
  handleEmptyListNavigation: PropTypes.func,
  onImageClick: PropTypes.func
};

ThumbnailsView.defaultProps = {
  images: null,
  url: null,
  handleEmptyListNavigation: null,
  onImageClick: undefined
};

ListImages.propTypes = {
  itemCount: PropTypes.number.isRequired,
  images: PropTypes.arrayOf(PropTypes.shape({
    url: PropTypes.string
  })),
  numberOfThumbnails: PropTypes.number.isRequired,
  url: PropTypes.string,
  disableThumbnailsLink: PropTypes.bool,
  viewMode: PropTypes.oneOf(['grid', 'thumbnails']),
  handleEmptyListNavigation: PropTypes.func,
  onImageClick: PropTypes.func
};

ListImages.defaultProps = {
  images: null,
  url: null,
  disableThumbnailsLink: false,
  viewMode: 'grid',
  handleEmptyListNavigation: null,
  onImageClick: undefined
};

export default ListImages;